const EXPERIMENTS = {
  checkoutNow: {
    defaultEnabled: false,
    growthbookKey: 'checkout-now',
    buttonSelector: '.js-checkout-now-experiment-button',
  },
};

const TELEMETRY = {
  experimentImpressionEvent: 'icse:experiment_impression',
};

const state = {};
const pendingExperimentImpressions = new Set();

function getButtonText(button, enabled) {
  return enabled ? button.dataset.treatmentText : button.dataset.controlText;
}

function getCheckoutTermsNote(button) {
  const group = button.closest('.collation-buttons, .mobile-sticky-wrapper, .book-course, .course-schedule__booking-action, .course-schedule__timetable-action, .course-schedule__booking');
  if (!group) return null;

  return group.querySelector('.checkout-now-terms-note')
    || (group.nextElementSibling?.matches('.checkout-now-terms-note') ? group.nextElementSibling : null);
}

function applyCheckoutNow(enabled) {
  document.querySelectorAll('add-to-cart-component').forEach((container) => {
    if (!(container instanceof HTMLElement)) return;

    container.dataset.directCheckout = enabled ? 'true' : 'false';

    const text = getButtonText(container, enabled);
    const textElement = container.querySelector('.add-to-cart-text__content');

    if (text && textElement) {
      textElement.textContent = text;
    }

    const note = container.querySelector('.checkout-now-terms-note');
    if (note) note.hidden = !enabled;
  });

  document.querySelectorAll(EXPERIMENTS.checkoutNow.buttonSelector).forEach((button) => {
    if (!(button instanceof HTMLElement)) return;

    const text = getButtonText(button, enabled);

    if (text) {
      button.textContent = text;
    }

    const note = getCheckoutTermsNote(button);
    if (note) note.hidden = !enabled;
  });
}

/**
 * Publish a GrowthBook assignment to Shopify Customer Events.
 * GrowthBook invokes this callback with the evaluated experiment and variation.
 * The session gate prevents duplicate network events for the same experiment.
 *
 * @param {{key: string}} experiment - GrowthBook experiment assignment
 * @param {{key: string|number}} result - Assigned GrowthBook variation
 */
function publishExperimentImpression(experiment, result) {
  const currentExperimentName = experiment?.key;
  const userVariation = result?.key;

  if (currentExperimentName == null || userVariation == null) return;

  const sessionKey = `gb_experiment_${currentExperimentName}_fired`;

  if (sessionStorage.getItem(sessionKey) || pendingExperimentImpressions.has(sessionKey)) return;

  const publish = window.Shopify?.analytics?.publish;
  if (typeof publish !== 'function') return;

  const payload = {
    experiment_name: currentExperimentName,
    experiment_value: userVariation,
  };

  pendingExperimentImpressions.add(sessionKey);

  let publishResult;
  try {
    publishResult = publish.call(
      window.Shopify.analytics,
      TELEMETRY.experimentImpressionEvent,
      payload,
    );
  } catch (error) {
    pendingExperimentImpressions.delete(sessionKey);
    console.error('[ICSEExperiments] Failed to publish experiment impression:', error);
    return Promise.resolve(false);
  }

  return Promise.resolve(publishResult)
    .then((wasDispatched) => {
      if (wasDispatched === false) {
        console.warn('[ICSEExperiments] Shopify did not dispatch experiment impression:', payload);
        return false;
      }

      sessionStorage.setItem(sessionKey, 'true');
      console.log('[ICSEExperiments] Published to Shopify Customer Events:', payload);
      return true;
    })
    .catch((error) => {
      console.error('[ICSEExperiments] Failed to publish experiment impression:', error);
      return false;
    })
    .finally(() => {
      pendingExperimentImpressions.delete(sessionKey);
    });
}

function apply(name) {
  if (name === 'checkoutNow') {
    applyCheckoutNow(Boolean(state.checkoutNow));
  }
}

function set(name, enabled) {
  if (!Object.prototype.hasOwnProperty.call(EXPERIMENTS, name)) return;

  state[name] = Boolean(enabled);
  apply(name);
}

function isEnabled(name) {
  return Boolean(state[name]);
}

function redirectCartToCheckout() {
  const form = document.createElement('form');
  const checkoutInput = document.createElement('input');

  form.method = 'post';
  form.action = '/cart';
  form.style.display = 'none';

  checkoutInput.type = 'hidden';
  checkoutInput.name = 'checkout';
  checkoutInput.value = 'Checkout';

  form.appendChild(checkoutInput);
  document.body.appendChild(form);
  form.submit();
}

window.ICSEExperiments = {
  set,
  apply,
  isEnabled,
  redirectCartToCheckout,
  publishExperimentImpression,
  override: set, // Generic override method for clean, centralized console testing of any experiment
};

// Backwards-compatible legacy helper
window.setCheckoutNowExperiment = (enabled) => {
  window.ICSEExperiments.set('checkoutNow', enabled);
};

// Initialize with default states
Object.entries(EXPERIMENTS).forEach(([name, config]) => {
  set(name, config.defaultEnabled);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => apply('checkoutNow'));
} else {
  apply('checkoutNow');
}

// Integrate GrowthBook SDK queue
window.growthbook_queue = window.growthbook_queue || [];
window.growthbook_queue.push((gb) => {
  gb.setTrackingCallback(publishExperimentImpression);

  const evaluateAll = () => {
    Object.entries(EXPERIMENTS).forEach(([name, config]) => {
      if (config.growthbookKey) {
        const enabled = gb.isOn(config.growthbookKey);
        window.ICSEExperiments.set(name, enabled);
      }
    });
  };

  // Run initial evaluation
  evaluateAll();

  // Re-run evaluation if GrowthBook receives real-time updates or attribute changes
  gb.setRenderer(() => {
    evaluateAll();
  });
});
